import React, { useContext, useState } from 'react';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { momentParseDate } from '../../../../helpers';
import { formatNumber } from '../../../../utils/utils';
import { Link } from 'react-router-dom';
import circleRed from '../../../../assets/images/icons/circle-color-red.svg';
import circleGreen from '../../../../assets/images/icons/circle-color-green.svg';
import circleGrey from '../../../../assets/images/icons/circle-color-grey.svg';
import { useScreenDetector } from '../../../../hooks/useScreenDetector';
import { FundsRequestDataForm, FundsFilters, FundRequestStatus, MassiveStatusForm, defaultFundRequestStatus } from '../../../../app/models/Fund';
import FundsRequestFilter from './FundsRequestFilter';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import FundRequestChangeStatus from '../FundRequestChangeStatus';
import ChangeStatusRows from '../../../../components/ChangeStatusRows';
import { StatusRows } from '../../../../app/shared/interfaces';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../../contexts/AppContext';
import useFundsRequestService from '../../../../app/services/hooks/useFundsRequestService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import DefaultModal from '../../../../components/default/DefaultModal';
import IssueDateForm from '../forms/IssueDateForm';
import { Society } from '../../../../app/models/Society';


interface Props {
    fundsRequests: FundsRequestDataForm[];
    totalRows: number;
    statuses: FundRequestStatus[];
    societies?: Society[];
    loading?: boolean;
    callbackSuccess?: () => void;
    filter: FundsFilters;
    setFilter: (filter: FundsFilters) => void;
    onSearch: () => void;
    destroy?: (id: number) => void;
    edit?: (quoteId: number) => void;
    reloadTable?: () => void;
    downloadTxt?: boolean
}

const FundsRequestDataTable = ({
    fundsRequests,
    totalRows,
    statuses,
    societies,
    loading = false,
    callbackSuccess,
    filter,
    setFilter,
    onSearch,
    destroy,
    edit,
    reloadTable,
    downloadTxt=false
}: Props) => {

    const { 
        changeFundRequestStatusesRows,
        massiveDeleteFundRequestRows
    } = useFundsRequestService();
 
    const { showLoading, hideLoading } = useContext(AppContext);
    const [rows, setRows] = useState<any[]>([]);
    const [toggleCleared, setToggleCleared] = useState<boolean>(false);
    const [showIssueDate, setShowIssueDate] = useState<boolean>(false);
    const [newStatusRows, setNewStatusRows] = useState<FundRequestStatus>(defaultFundRequestStatus);
    const [errorFields, setErrorFields] = useState<any>();
    const [massiveStatusForm, setMassiveStatusForm] = useState<MassiveStatusForm>({
        all_selected: false,
        selected_count: 0,
        rows_ids: []
    });

    const handleRowSelected = React.useCallback(
        ({ allSelected, selectedCount, selectedRows }: any) => {
            setRows(selectedRows);
            setMassiveStatusForm(prev => ({
                ...prev,
                all_selected: false,
                selected_count: selectedCount,
                rows_ids: selectedRows.map((row: any) => row.id)
            }));
        },
        []
    );

    const contextMessage = ({
        selectedCount,
    }: {
        selectedCount: number;
    }) => {
        return {
            singular: 'fila seleccionada',
            plural: 'filas seleccionadas'
        };
    };

    const resetContextActions = () => {
        setToggleCleared(!toggleCleared);
        setRows([]);
        hideIssueDate();
    };

    
    const showIssueDateModal = () => {
        setShowIssueDate(true);
    };

    const hideIssueDate = () => {
        setShowIssueDate(false);
    };

    const confirmChangeRows = (newStatus: FundRequestStatus) => {

        const _text = `Está a punto de cambiar el estado de los registros marcados a ${newStatusRows!.label}`;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Cambiando estados...');

                        changeFundRequestStatusesRows(
                            {
                                ...massiveStatusForm,
                                status: newStatus.id?.toString(),
                                issue_date: newStatus.issue_date ?? undefined,
                                society_bank_account_id: newStatus.society_bank_account_id ?? undefined
                            },
                            {
                                onSuccess: (response: ServiceResponse) => {
                                    if (hideLoading) hideLoading();

                                    useReactConfirmAlert().successAlert({
                                        title: 'Éxito',
                                        message: response.message,
                                        message_list: response.data?.message_list ?? []
                                    });
                                    resetContextActions();
                                    if(reloadTable) reloadTable();
                                },
                                onError: (response: ServiceResponse) => {
                                    if (hideLoading) hideLoading();

                                    useReactConfirmAlert().errorAlert({
                                        title: 'Error',
                                        message: response.message
                                    });
                                },
                                onFieldError: (errorFields: ServiceResponse) => {
                                    if (hideLoading) hideLoading();
                                    setErrorFields(errorFields.data);
                                },
                            }
                        );
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'Los estados no se han cambiado.'
                            });
                        }, 0);
                    }
                }
            }
        });

    };
    
    const changeRowsStatus = (
        value: string,
        massiveChange: boolean,
        setSelected: (status: string) => void
    ) => {
        if (value === '') {
            setSelected('');
            return;
        }

        const _status = statuses.find(
            (status: FundRequestStatus) => status.label == value
        );

        if(_status){

            setNewStatusRows(_status);
    
            if(_status.id != 'CONFIRM'){
                confirmChangeRows(_status);

            } else{
                // Validar que todas las filas tengan la misma sociedad
                if (rows.length === 0) {
                    useReactConfirmAlert().infoAlert({
                        title: 'Información',
                        message: 'No hay filas seleccionadas para confirmar.'
                    });
                    return;
                }

                const societies = rows.map((row: FundsRequestDataForm) => row.society_id);
                const uniqueSocieties = [...new Set(societies)];
                
                if (uniqueSocieties.length > 1) {
                    useReactConfirmAlert().errorAlert({
                        title: 'Error de validación',
                        message: 'Todas las solicitudes seleccionadas deben pertenecer a la misma sociedad para poder confirmarlas en conjunto.'
                    });
                    setSelected('');
                    return;
                }

                // Guardar la sociedad para usarla en el modal
                const firstRow = rows[0] as FundsRequestDataForm;
                setNewStatusRows({
                    ..._status, 
                    society_id: firstRow.society_id,
                    society: firstRow.society || undefined
                });
                
                showIssueDateModal();
            }
        }
    };


    const massiveDelete = () => {
        if(massiveStatusForm){
            if (rows.length === 0) {
                useReactConfirmAlert().infoAlert({
                    title: 'Información',
                    message: 'No hay filas seleccionadas para eliminar.'
                });
                return;
            }
        
            const confirmationMessage = `Está a punto de eliminar ${
                massiveStatusForm.all_selected ? 'todas las filas seleccionadas' : 'las filas seleccionadas'
            }. ¿Desea continuar?`;
        
            useReactConfirmAlert().requestConfirmation({
                title: '¿Estás seguro?',
                message: confirmationMessage,
                buttons: {
                    confirmButton: {
                        label: 'Sí, eliminar',
                        onClick: () => {
                            if (showLoading) showLoading('loading', 'Eliminando filas...');
        
                            massiveDeleteFundRequestRows(
                                {
                                    ...massiveStatusForm,
                                    rows_ids: rows.map(row => row.id),
                                },
                                {
                                    onSuccess: (response: ServiceResponse) => {
                                        if (hideLoading) hideLoading();
        
                                        useReactConfirmAlert().successAlert({
                                            title: 'Éxito',
                                            message: response.message,
                                            message_list: response.data?.message_list ?? []
                                        });
                                        resetContextActions();
                                        if (reloadTable) reloadTable();
                                    },
                                    onError: (response: ServiceResponse) => {
                                        if (hideLoading) hideLoading();
        
                                        useReactConfirmAlert().errorAlert({
                                            title: 'Error',
                                            message: response.message
                                        });
                                    },
                                    onFieldError: (response: ServiceResponse) => {
                                        if (hideLoading) hideLoading();
        
                                        useReactConfirmAlert().errorAlert({
                                            title: 'Error',
                                            message: response.message
                                        });
                                    }
                                }
                            );
                        }
                    },
                    cancelButton: {
                        label: 'No, cancelar',
                        onClick: () => {
                            setTimeout(() => {
                                useReactConfirmAlert().infoAlert({
                                    title: 'Cancelado',
                                    message: 'Las filas no se han eliminado.'
                                });
                            }, 0);
                        }
                    }
                }
            });
        }
    };


    const columns = [
        {
            name: 'Id',
            selector: (row: any) => row?.id,
            sortable: true,
            sortField: 'id',
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                <span className="">{row.id}</span>
            )
        },
        {
            name: 'Conductor',
            selector: (row: any) => row?.worker_name,
            sortable: true,
            sortField: 'worker',
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                <span className="">{row.worker_name}</span>
            )
        },
        {
            name: 'Motivo',
            selector: (row: any) => row?.transfer_reason_id,
            sortable: true,
            sortField: 'transfer_reason',
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                <span className="">{row.transfer_reason}</span>
            )
        },  
        {
            name: 'Society',
            selector: (row: any) => row?.society,
            sortable: true,
            sortField: 'society',
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                <span className="">{row.society}</span>
            )
        },  
        {
            name: 'Fecha',
            selector: (row: any) => row?.date,
            sortable: true,
            sortField: 'date',
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                <span className="">{momentParseDate(row?.date)}</span>
            )
        },
        {
            name: 'Total',
            selector: (row: any) => row?.total,
            sortable: true,
            sortField: 'total',
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                <>
                    <span className="">
                        {formatNumber(row.total ? Number(row.total) : 0)} {row.currency}
                    </span>
                </>
            )
        },
        {
            name: 'Solicitado por',
            selector: (row: any) => row?.created_user,
            sortable: true,
            sortField: 'created_user',
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                    <span className="">{row.created_user}</span>
            )
        },
        {
            name: 'Estado',
            selector: (row: any) => row.status,
            sortable: true,
            sortField: 'status',
            omit: downloadTxt,
            cell: (row: FundsRequestDataForm, index: any, column: any) => {
                const status = statuses.find((fRStatus) => fRStatus.id === row.status);
                if (statuses.length == 0) {
                    return '';
                }
                
                return (
                    <FundRequestChangeStatus
                        callbackSuccess={callbackSuccess}
                        statuses={row.next_status_options!}
                        selectedStatus={status!}
                        fundRequestId={row.id!}
                        fundsRequest={row}
                    />
                );
            }
        },
        {
            name: 'Acciones',
            selector: (row: any) => row?.id,
            sortable: false,
            width: '150px',
            omit: downloadTxt,
            cell: (row: FundsRequestDataForm, index: any, column: any) => (
                <div className="">
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row.id ?? -1)}
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            errorMessage={'No se puede editar este registro.'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row?.id ?? -1)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            errorMessage={'No se puede eliminar este registro.'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            )
        }

    ];


    const contextActions = React.useMemo(() => {
        return (
            <div className="d-flex">
            <div className="me-2">
                <ChangeStatusRows
                    changeStatus={changeRowsStatus}
                    className="form-select-sm"
                    statuses={statuses}
                />
            </div>
            <div>
                <button
                    className="btn btn-danger btn-sm"
                    onClick={massiveDelete}
                >
                    Eliminar filas
                </button>
            </div>
        </div>
        );
    }, [fundsRequests, rows, toggleCleared]);



    const { isDesktop } = useScreenDetector();

    return (
        <>
            <div className="row mt-3">
                <div className="col mb-3">
                    <FundsRequestFilter 
                        filter={filter} 
                        setFilter={setFilter} 
                        onSearch={onSearch}
                        societies={societies}
                        downloadTxt={downloadTxt}
                    />
                </div>
            </div>

            <DefaultDataTable
                title="Solicitudes de Fondos"
                columns={columns}
                data={fundsRequests}
                progressPending={loading}
                isMobileStacked={true}
                paginationTotalRows={totalRows}
                filter={filter}
                setFilter={setFilter}
                selectableRowsHighlight={!downloadTxt}
                selectableRows={!downloadTxt}
                onSelectedRowsChange={handleRowSelected}
                clearSelectedRows={toggleCleared}
                contextActions={contextActions}
                contextMessage={contextMessage({
                    selectedCount: massiveStatusForm?.selected_count ?? 0,
                })}
            />
            
            {showIssueDate && (
                <DefaultModal
                    show={showIssueDate}
                    handleClose={hideIssueDate}
                    title="Añadir fecha de emisión de las transferencias"
                    backdrop={true}
                    showFooter={false}
                >
                    <IssueDateForm
                        status={newStatusRows} 
                        setStatus={setNewStatusRows}
                        onSaved={(updatedStatus?: FundRequestStatus) => {
                            if (updatedStatus) {
                                setNewStatusRows(updatedStatus);
                                confirmChangeRows(updatedStatus);
                            } else {
                                confirmChangeRows(newStatusRows);
                            }
                        }}
                        onCancel={hideIssueDate}
                        onError={hideIssueDate}   
                        errorFields={errorFields}       
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default FundsRequestDataTable;
